import { lazy, Suspense, type ComponentType } from 'react';
import { createBrowserRouter, Outlet } from 'react-router-dom';

import { ROUTES } from '@/constants/routes';
import { ProtectedRoute } from '@/app/ProtectedRoute';
import { RouteErrorBoundary } from '@/components/feedback/RouteErrorBoundary';
import { PageLoader } from '@/components/feedback/PageLoader';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';

// Route-level code splitting: each page is its own async chunk.
const DashboardPage = lazy(() => import('@/features/dashboard/pages/DashboardPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const AuthCallbackPage = lazy(() => import('@/features/auth/pages/AuthCallbackPage'));
const WelcomePage = lazy(() => import('@/features/welcome/pages/WelcomePage'));
const UsersPage = lazy(() => import('@/features/users/pages/UsersPage'));
const MyPortfolioPage = lazy(() => import('@/features/portfolio/pages/MyPortfolioPage'));
const PortfoliosPage = lazy(() => import('@/features/portfolio/pages/PortfoliosPage'));
const TermsPage = lazy(() => import('@/features/legal/pages/TermsPage'));
const MyProfilePage = lazy(() => import('@/features/profile/pages/MyProfilePage'));
const IncomeCalendarPage = lazy(() => import('@/features/income/pages/IncomeCalendarPage'));
// Performance (SC-PTF-03) descoped — not required (docs Rev 1.2, PC-09). Import kept dormant.
// const PerformancePage = lazy(() => import('@/features/performance/pages/PerformancePage'));
// Mutual Fund tab hidden per request (2026-09-21) — pending resolution. Import kept dormant.
// const MutualFundPage = lazy(() => import('@/features/legal/pages/MutualFundPage'));
const HoldingsPage = lazy(() => import('@/features/holdings/pages/HoldingsPage'));
const TransactionsPage = lazy(() => import('@/features/transactions/pages/TransactionsPage'));
const AccountInfoPage = lazy(() => import('@/features/account-info/pages/AccountInfoPage'));

/** Wrap a lazily-loaded page in a Suspense boundary with a shared loader. */
function withSuspense(Component: ComponentType): React.ReactElement {
  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );
}

export const router = createBrowserRouter([
  {
    element: <Outlet />,
    errorElement: <RouteErrorBoundary />,
    children: [
      // --- OAuth callback (public) --------------------------------------------
      // The exact `redirect_uri` Barita sends the user back to. Not behind
      // ProtectedRoute: it's the only route that ever reads token/state off
      // the URL, and it must be reachable while signed out.
      { path: ROUTES.authCallback, element: withSuspense(AuthCallbackPage) },
      // --- Protected routes ----------------------------------------------------
      {
        element: <ProtectedRoute />,
        children: [
          // Portal landing / welcome (SC-LOG-04) — own chrome, no dashboard nav.
          { path: ROUTES.welcome, element: withSuspense(WelcomePage) },
          {
            element: <DashboardLayout />,
            children: [
              { path: ROUTES.home, element: withSuspense(DashboardPage) },
              { path: ROUTES.portfolios, element: withSuspense(MyPortfolioPage) },
              { path: ROUTES.portfolioList, element: withSuspense(PortfoliosPage) },
              { path: ROUTES.terms, element: withSuspense(TermsPage) },
              { path: ROUTES.profile, element: withSuspense(MyProfilePage) },
              { path: ROUTES.incomeCalendar, element: withSuspense(IncomeCalendarPage) },
              // Performance (SC-PTF-03) descoped — not required (docs Rev 1.2, PC-09).
              // Route disabled so /performance falls through to NotFound; page code kept dormant.
              // { path: ROUTES.performance, element: withSuspense(PerformancePage) },
              // Mutual Fund tab hidden per request (2026-09-21) — pending resolution.
              // Route disabled so /mutual-fund falls through to NotFound; page code kept dormant.
              // { path: ROUTES.mutualFund, element: withSuspense(MutualFundPage) },
              { path: ROUTES.holdings, element: withSuspense(HoldingsPage) },
              { path: ROUTES.transactions, element: withSuspense(TransactionsPage) },
              { path: ROUTES.accountInfo, element: withSuspense(AccountInfoPage) },
              { path: ROUTES.users, element: withSuspense(UsersPage) },
            ],
          },
        ],
      },
      // --- Catch-all ---------------------------------------------------------
      { path: '*', element: withSuspense(NotFoundPage) },
    ],
  },
]);
